import { parseImages } from "@/plano/utils/parse-catalog";
import Globals from "@/components/globals";
import { NONE_HIGHLIGHT } from "@/plano/elements";
import { Context } from "plano_engine";

export const CORRUPTED_MODULE_ORIGIN = {
    planogram: "planogram",
    catalog: "catalog"
};

/**
 * Ces components Types sont censés être dynamiques mais l'application a besoin de savoir exactement sur quel type d'éléments opérés.
 * Ils sont écris en dure en tant que constante. Voir aussi @/services/components.js qui aussi deux constante pour les types de composants
 * COMPONENT_TYPE et COMPONENT_NAME
 * @type {{imageFascia: string, ladder: string, module: string, doubleLadder: string, caddy: string, imageCoverVisual: string, shelf: string}}
 */
export const COMPONENT_TYPE = {
    module: "ModuleElement",
    shelf: "Shelf",
    caddy: "Caddy",
    doubleLadder: "DoubleLadder",
    ladder: "Ladder",
    imageCoverVisual: "ImageCoverVisual",
    imageFascia: "ImageFascia",
    imageBlankingGraphic: "ImageBlankingGraphic"
};

export const COMMENT_ICON_INDEX = 1;
export function modelForId(config, id) {
    return config.models.find(model => model.id === id);
}

export function centerCarcass() {
    setTimeout(() => {
        if (Context.table.paper.children()[0] && Context.table.paper.children()[0].children()[0]) {
            Context.selection.selectOne(Context.table.paper.children()[0].children()[0]);
        }
        Context.zoomFitPaper();
        if (Context.table.paper.children()[0] && Context.table.paper.children()[0].children()[0]) {
            Context.selection.unselect();
        }
    }, 100);
}

/**
 * Retire les liens avec les fixings de tous les caddies
 * @param config
 */
export function cleanClipRefElementsInScratchpad(config) {
    for (const index in config.content.children) {
        delete config.content.children[index].clipped;
    }
}

/**
 * Trouve toutes les specs d'un type donné. Le type passé en paramètre est un tableau composé d'élément
 * se trouvant dans COMPONENT_TYPE juste au dessus
 * @param config
 * @param componentTypes
 * @returns {Array<{ spec: Spec, model: Model }>}
 */
export function findAllComponentsSpecByType(config, componentTypes, { includeScratchpad } = {}) {
    const res = [];

    visitSpecs(config.content.paper, spec => {
        const model = modelForId(config, spec.model);
        if (model && componentTypes.includes(model.type)) {
            res.push({ spec, model });
        }
    });
    if (includeScratchpad) {
        visitSpecs(config.content, spec => {
            const model = modelForId(config, spec.model);
            if (model && componentTypes.includes(model.type)) {
                res.push({ spec, model });
            }
        });
    }
    return res;
}

export function visitSpecs(config, visitor) {
    visitor(config);
    if (config.children) {
        for (const child of config.children) {
            visitSpecs(child, visitor);
        }
    }
}

/**
 * Met à jour un objet 'tester' passé en paramètre
 * @param highlightsDetails <Array { id: String, code: String, label: String } >
 * @param highlightTester <Object[tester]{ color: {id: String, code: String, label: String}, rowId: String }>
 * @returns {*}
 */
function updateHighlightDatas(highlightsDetails, highlightTester) {
    for (const key in highlightTester) {
        let highlightObject = highlightTester[key];

        const { id } = highlightObject.color;
        const highlightFromBrand = highlightsDetails.find(color => color.id === id);

        if (highlightFromBrand) {
            highlightObject.color = highlightFromBrand || NONE_HIGHLIGHT;
        }
        highlightTester[key] = highlightObject;
    }

    return highlightTester;
}

/**
 * Met à jour les highlights en fonction de leur Id
 * @param highlightsDetails
 * @param specs
 * @returns {[] || undefined}
 */

export function updateHihlightDetailsInModuleSpecs(highlightsDetails, specs) {
    if (!specs) return undefined;

    for (const index in specs) {
        const spec = specs[index] && specs[index].spec;

        if (spec && spec.highlightTester && !Array.isArray(spec.highlightTester)) {
            spec.highlightTester = updateHighlightDatas(highlightsDetails, spec.highlightTester);
        }
        specs[index].spec = spec;
    }

    return specs;
}

/**
 * Détermine si un model est un visuel ou pas
 * @param modelType
 * @returns {boolean}
 */
export function isVisual(modelType) {
    return [COMPONENT_TYPE.imageCoverVisual, COMPONENT_TYPE.imageFascia].includes(modelType);
}

/**
 * Récupère tous les models de type visuel d'un planogramme
 * @param models
 * @returns {Set<any>}
 */
export function visualsModelFromPlanogram(models) {
    const res = new Set();
    for (const model of models) {
        if (isVisual(model.type)) {
            res.add(model.id);
        }
    }
    return res;
}

/**
 * config étant le JSON du planogramme, il inject les nouvelles informations envoyées par l'API dans le planogramme pour mettre
 * ses éléments à jour.
 * @param config
 * @param elementDetails
 */
export function injectElementDetailsInModels(config, elementDetails) {
    for (const model of config.models) {
        if (model.type !== COMPONENT_TYPE.module && !isVisual(model.type)) continue;

        const elementDetail = elementDetails.find(d => d.id === model.id);

        if (model.type == COMPONENT_TYPE.module) {
            if (!elementDetail) {
                console.error("Failed to obtain module details", model.id, elementDetails);
                let event = new Event("missing-plano-module");
                event.module = { id: model.id, missing: true };
                event.imageURL = undefined;
                event.origin = CORRUPTED_MODULE_ORIGIN.planogram;
                window.dispatchEvent(event);
                continue;
            }
            const [imageURL, realisticURL] = parseImages(elementDetail);
            if (!realisticURL) console.warn(`Module ${elementDetail.id} has no realistic image`, elementDetail);
            if (!imageURL) {
                let event = new Event("missing-plano-module");
                event.module = elementDetail;
                event.imageURL = imageURL;
                event.origin = CORRUPTED_MODULE_ORIGIN.planogram;
                window.dispatchEvent(event);
            }
            Object.assign(model, { color: "rbg(0, 0, 0)", imageURL, realisticURL });
        } else if (isVisual(model.type)) {
            if (!elementDetail) {
                console.error("Failed to obtain some visuals details", model.id);
                continue;
            }
            const [imageURL, plainURL] = parseImages(elementDetail);
            Object.assign(model, { imageURL, plainURL });
        } else if (model.type === COMPONENT_TYPE.imageBlankingGraphic) {
            if (!elementDetail) {
                console.error("Failed to obtain some blanking graphic details", model.id);
                continue;
            }
            const [imageURL, plainURL] = parseImages(elementDetail);
            Object.assign(model, { imageURL, plainURL });
        }
    }
}

/**
 * Return a Set of images from a module
 * @param module
 * @returns {Set<String>}
 */
export function fetchAllImagesInModules(module) {
    let images = new Set();
    const imageFormat = ["small", "medium", "big"];
    const figures = Object.values(module.data.figures);
    for (const format of imageFormat) {
        figures[format] && images.add(figures[format]); // Main Image
        figures[`${format}Mob`] && images.add(Globals.getBaseUrl() + figures[`${format}Mob`]); //Image in Mobile format
    }
    for (const img of Object.values(module.data.figures_advanced)) {
        for (const format of imageFormat) img[format] && images.add(Globals.getBaseUrl() + img[format]);
    }
    return images;
}

/**
 * Apply comment Tips on every elements
 * @param elements
 * @param plano
 */
export function addCommentTipsInPlanogramme(elements, plano) {
    for (const commentObject of plano.commentCountByExtId) {
        const element = elements.find(element => element.id === commentObject.componentIdExternal);
        if (element) {
            applyTipCommentOnElement(element);
        }
    }
}

/**
 * Apply comment tip on element
 * @param element
 */
export function applyTipCommentOnElement(element) {
    console.assert(element);
    if (element) element.addDecoration(assets.icons.commentsTip, COMMENT_ICON_INDEX, undefined);
}

/**
 * Remove comment tip on element
 * @param element
 */
export function removeCommentTipCommentOnElement(element) {
    console.assert(element);
    if (element) element.removeDecoration(COMMENT_ICON_INDEX);
}



// WEBPACK FOOTER //
// ./src/plano/utils/plano.js